import type {
  Permission,
  PermissionMatrix,
  PermissionMetadata,
  PermissionDomain,
  StudioRole,
} from '@/types/permissions'

/**
 * Default permission matrix for all roles
 *
 * Design principles:
 * 1. Member: Minimal self-service permissions
 * 2. Instructor: Member + teaching and attendance
 * 3. Manager: Instructor + full studio operations (except role config)
 * 4. Owner: ALL permissions including role configuration
 *
 * Owners ALWAYS have all permissions - no overrides possible
 */
export const DEFAULT_PERMISSION_MATRIX: PermissionMatrix = {
  member: [
    // Schedule - self-service only
    'schedule:view',
    'schedule:book-self',
    'schedule:cancel-self',

    // Classes - view only
    'classes:view',

    // Billing - own data only
    'billing:view-own',
  ],

  instructor: [
    // Schedule - self-service + teaching
    'schedule:view',
    'schedule:book-self',
    'schedule:cancel-self',

    // Members - view for teaching purposes
    'members:view-list',
    'members:view-details',

    // Classes - teaching capabilities
    'classes:view',
    'classes:teach',
    'classes:view-attendance',
    'classes:mark-attendance',

    // Curriculum - full access for own classes
    'curriculum:view',
    'curriculum:create',
    'curriculum:edit',
    'curriculum:assign',

    // Billing - own data only
    'billing:view-own',

    // Reporting - attendance only
    'reporting:view-attendance',
  ],

  manager: [
    // Schedule - full management
    'schedule:view',
    'schedule:book-self',
    'schedule:book-others',
    'schedule:cancel-self',
    'schedule:cancel-others',

    // Members - full management except role assignment
    'members:view-list',
    'members:view-details',
    'members:invite',
    'members:remove',
    'members:edit-status',
    'members:view-notes',
    'members:edit-notes',

    // Classes - full management
    'classes:view',
    'classes:create',
    'classes:edit',
    'classes:delete',
    'classes:teach',
    'classes:view-attendance',
    'classes:mark-attendance',

    // Curriculum - full management
    'curriculum:view',
    'curriculum:create',
    'curriculum:edit',
    'curriculum:delete',
    'curriculum:assign',

    // Billing - full management except refunds
    'billing:view-own',
    'billing:view-all',
    'billing:manage-plans',
    'billing:process-payments',
    'billing:view-reports',

    // Settings - basic settings only
    'settings:view',
    'settings:edit-general',
    'settings:edit-pricing',

    // Reporting - full access
    'reporting:view-attendance',
    'reporting:view-revenue',
    'reporting:view-member-stats',
    'reporting:export',
  ],

  owner: [
    // Schedule - all
    'schedule:view',
    'schedule:book-self',
    'schedule:book-others',
    'schedule:cancel-self',
    'schedule:cancel-others',

    // Members - all including role assignment
    'members:view-list',
    'members:view-details',
    'members:invite',
    'members:remove',
    'members:edit-role',
    'members:edit-status',
    'members:view-notes',
    'members:edit-notes',

    // Classes - all
    'classes:view',
    'classes:create',
    'classes:edit',
    'classes:delete',
    'classes:teach',
    'classes:view-attendance',
    'classes:mark-attendance',

    // Curriculum - all
    'curriculum:view',
    'curriculum:create',
    'curriculum:edit',
    'curriculum:delete',
    'curriculum:assign',

    // Billing - all including refunds
    'billing:view-own',
    'billing:view-all',
    'billing:manage-plans',
    'billing:process-payments',
    'billing:issue-refunds',
    'billing:view-reports',

    // Settings - all including role management
    'settings:view',
    'settings:edit-general',
    'settings:edit-roles',
    'settings:edit-pricing',

    // Reporting - all
    'reporting:view-attendance',
    'reporting:view-revenue',
    'reporting:view-member-stats',
    'reporting:export',
  ],
}

/**
 * Get all available permissions
 */
export const ALL_PERMISSIONS: Permission[] = DEFAULT_PERMISSION_MATRIX.owner

/**
 * Permission metadata for UI display
 */
export const PERMISSION_METADATA: Record<
  Permission,
  Omit<PermissionMetadata, 'permission'>
> = {
  // Schedule
  'schedule:view': {
    domain: 'Schedule',
    label: 'View Schedule',
    description: 'View studio class schedule',
    requiresOwner: false,
  },
  'schedule:book-self': {
    domain: 'Schedule',
    label: 'Book Own Classes',
    description: 'Book classes for yourself',
    requiresOwner: false,
  },
  'schedule:book-others': {
    domain: 'Schedule',
    label: 'Book for Others',
    description: 'Book classes on behalf of other members',
    requiresOwner: false,
    isRisky: true,
  },
  'schedule:cancel-self': {
    domain: 'Schedule',
    label: 'Cancel Own Bookings',
    description: 'Cancel your own class bookings',
    requiresOwner: false,
  },
  'schedule:cancel-others': {
    domain: 'Schedule',
    label: 'Cancel for Others',
    description: 'Cancel bookings for other members',
    requiresOwner: false,
    isRisky: true,
  },

  // Members
  'members:view-list': {
    domain: 'Members',
    label: 'View Member List',
    description: 'View the studio member directory',
    requiresOwner: false,
  },
  'members:view-details': {
    domain: 'Members',
    label: 'View Member Details',
    description: 'View detailed member profiles',
    requiresOwner: false,
  },
  'members:invite': {
    domain: 'Members',
    label: 'Invite Members',
    description: 'Invite new members to the studio',
    requiresOwner: false,
  },
  'members:remove': {
    domain: 'Members',
    label: 'Remove Members',
    description: 'Remove members from the studio',
    requiresOwner: false,
    isRisky: true,
  },
  'members:edit-role': {
    domain: 'Members',
    label: 'Edit Member Roles',
    description: 'Change member roles (instructor, manager)',
    requiresOwner: true,
  },
  'members:edit-status': {
    domain: 'Members',
    label: 'Edit Member Status',
    description: 'Change member status (active, paused, cancelled)',
    requiresOwner: false,
  },
  'members:view-notes': {
    domain: 'Members',
    label: 'View Private Notes',
    description: 'View private notes on member profiles',
    requiresOwner: false,
  },
  'members:edit-notes': {
    domain: 'Members',
    label: 'Edit Private Notes',
    description: 'Add or edit private notes on member profiles',
    requiresOwner: false,
  },

  // Classes
  'classes:view': {
    domain: 'Classes',
    label: 'View Classes',
    description: 'View class information and details',
    requiresOwner: false,
  },
  'classes:create': {
    domain: 'Classes',
    label: 'Create Classes',
    description: 'Create new class types',
    requiresOwner: false,
  },
  'classes:edit': {
    domain: 'Classes',
    label: 'Edit Classes',
    description: 'Edit existing class details',
    requiresOwner: false,
  },
  'classes:delete': {
    domain: 'Classes',
    label: 'Delete Classes',
    description: 'Delete class types',
    requiresOwner: false,
    isRisky: true,
  },
  'classes:teach': {
    domain: 'Classes',
    label: 'Teach Classes',
    description: 'Be assigned as instructor for classes',
    requiresOwner: false,
  },
  'classes:view-attendance': {
    domain: 'Classes',
    label: 'View Attendance',
    description: 'View class attendance records',
    requiresOwner: false,
  },
  'classes:mark-attendance': {
    domain: 'Classes',
    label: 'Mark Attendance',
    description: 'Mark members as present or absent',
    requiresOwner: false,
  },

  // Curriculum
  'curriculum:view': {
    domain: 'Curriculum',
    label: 'View Curriculum',
    description: 'View curriculum and lesson plans',
    requiresOwner: false,
  },
  'curriculum:create': {
    domain: 'Curriculum',
    label: 'Create Curriculum',
    description: 'Create new curriculum and lesson plans',
    requiresOwner: false,
  },
  'curriculum:edit': {
    domain: 'Curriculum',
    label: 'Edit Curriculum',
    description: 'Edit existing curriculum and lesson plans',
    requiresOwner: false,
  },
  'curriculum:delete': {
    domain: 'Curriculum',
    label: 'Delete Curriculum',
    description: 'Delete curriculum and lesson plans',
    requiresOwner: false,
    isRisky: true,
  },
  'curriculum:assign': {
    domain: 'Curriculum',
    label: 'Assign Curriculum',
    description: 'Assign curriculum to class sessions',
    requiresOwner: false,
  },

  // Billing
  'billing:view-own': {
    domain: 'Billing',
    label: 'View Own Billing',
    description: 'View your personal billing and payments',
    requiresOwner: false,
  },
  'billing:view-all': {
    domain: 'Billing',
    label: 'View All Billing',
    description: 'View all studio billing records',
    requiresOwner: false,
  },
  'billing:manage-plans': {
    domain: 'Billing',
    label: 'Manage Plans',
    description: 'Create and edit membership plans',
    requiresOwner: false,
  },
  'billing:process-payments': {
    domain: 'Billing',
    label: 'Process Payments',
    description: 'Process manual payments and adjustments',
    requiresOwner: false,
  },
  'billing:issue-refunds': {
    domain: 'Billing',
    label: 'Issue Refunds',
    description: 'Issue refunds to members',
    requiresOwner: true,
    isRisky: true,
  },
  'billing:view-reports': {
    domain: 'Billing',
    label: 'View Billing Reports',
    description: 'View billing analytics and reports',
    requiresOwner: false,
  },

  // Settings
  'settings:view': {
    domain: 'Settings',
    label: 'View Settings',
    description: 'View studio settings',
    requiresOwner: false,
  },
  'settings:edit-general': {
    domain: 'Settings',
    label: 'Edit General Settings',
    description: 'Edit studio name, description, contact info',
    requiresOwner: false,
  },
  'settings:edit-roles': {
    domain: 'Settings',
    label: 'Manage Roles',
    description: 'Configure role permissions',
    requiresOwner: true,
  },
  'settings:edit-pricing': {
    domain: 'Settings',
    label: 'Edit Pricing',
    description: 'Configure pricing and billing settings',
    requiresOwner: false,
  },

  // Reporting
  'reporting:view-attendance': {
    domain: 'Reporting',
    label: 'View Attendance Reports',
    description: 'View attendance analytics',
    requiresOwner: false,
  },
  'reporting:view-revenue': {
    domain: 'Reporting',
    label: 'View Revenue Reports',
    description: 'View revenue and financial analytics',
    requiresOwner: false,
  },
  'reporting:view-member-stats': {
    domain: 'Reporting',
    label: 'View Member Statistics',
    description: 'View member growth and retention stats',
    requiresOwner: false,
  },
  'reporting:export': {
    domain: 'Reporting',
    label: 'Export Reports',
    description: 'Export report data to CSV or Excel',
    requiresOwner: false,
  },
}

/**
 * Permission domains with display info
 */
export const PERMISSION_DOMAINS: Record<
  PermissionDomain,
  { label: string; icon: string; order: number }
> = {
  Schedule: { label: 'Schedule Management', icon: 'calendar', order: 1 },
  Members: { label: 'Member Management', icon: 'users', order: 2 },
  Classes: { label: 'Class Management', icon: 'dumbbell', order: 3 },
  Curriculum: { label: 'Curriculum Management', icon: 'book-marked', order: 4 },
  Billing: { label: 'Billing & Payments', icon: 'credit-card', order: 5 },
  Settings: { label: 'Settings & Configuration', icon: 'settings', order: 6 },
  Reporting: { label: 'Reporting & Analytics', icon: 'bar-chart', order: 7 },
}

/**
 * Group permissions by domain for UI display
 */
export function getPermissionsByDomain(): Record<
  PermissionDomain,
  PermissionMetadata[]
> {
  const grouped = {} as Record<PermissionDomain, PermissionMetadata[]>

  for (const [permission, metadata] of Object.entries(PERMISSION_METADATA)) {
    const { domain } = metadata
    if (!grouped[domain]) {
      grouped[domain] = []
    }
    grouped[domain].push({
      permission: permission as Permission,
      ...metadata,
    })
  }

  return grouped
}

/**
 * Check if a role has a permission by default
 */
export function hasDefaultPermission(
  role: StudioRole,
  permission: Permission
): boolean {
  return DEFAULT_PERMISSION_MATRIX[role].includes(permission)
}

/**
 * Get permissions that can be toggled for a role
 * (excludes owner-only permissions for non-owner roles)
 */
export function getToggleablePermissions(
  _role: Exclude<StudioRole, 'owner'>
): Permission[] {
  // Note: Currently all roles have the same toggleable permissions
  // The role parameter is kept for future extensibility
  return ALL_PERMISSIONS.filter((permission) => {
    const metadata = PERMISSION_METADATA[permission]
    return !metadata.requiresOwner
  })
}

/**
 * Studio role configuration for display
 */
export const STUDIO_ROLE_CONFIG: Record<
  StudioRole,
  { label: string; description: string; color: 'zinc' | 'purple' | 'blue' | 'amber' }
> = {
  member: {
    label: 'Member',
    description: 'Can book classes and track personal training',
    color: 'zinc',
  },
  instructor: {
    label: 'Instructor',
    description: 'Can teach classes and view attendance',
    color: 'purple',
  },
  manager: {
    label: 'Manager',
    description: 'Full studio management access including billing',
    color: 'blue',
  },
  owner: {
    label: 'Owner',
    description: 'Studio owner with all permissions',
    color: 'amber',
  },
}
